import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminPesdbLink — schermata "Aggancia Ugly → PESDB".
 * Dopo un Sync veloce, collega le voci di rosa senza pes_id ai nuovi giocatori
 * PESDB (per nome, con conferma sui duplicati). Una volta agganciato, lo stipendio
 * usa automaticamente VG e ruolo reali.
 */
export default function AdminPesdbLink() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkedCount, setLinkedCount] = useState(0);
    const [showNoMatch, setShowNoMatch] = useState(false);
    const [msg, setMsg] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/pesdb-link/candidates");
            setData(data);
        } catch (e) {
            setMsg(e.response?.data?.detail || "Errore nel caricamento");
        } finally {
            setLoading(false);
        }
    };

    /* eslint-disable */
    useEffect(() => { load(); }, []);
    /* eslint-enable */

    const onLinked = (entryId, label) => {
        setData((d) => ({ ...d, entries: d.entries.filter((e) => e.entry_id !== entryId) }));
        setLinkedCount((c) => c + 1);
        setMsg(`Agganciato: ${label}`);
        setTimeout(() => setMsg(""), 4000);
    };

    if (loading) return <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>;
    if (!data) return <div className="op-mono text-tff4d4d">{msg}</div>;

    const withMatch = data.entries.filter((e) => e.candidates.length > 0);
    const noMatch = data.entries.filter((e) => e.candidates.length === 0);

    return (
        <div data-testid="admin-pesdb-link">
            <div className="op-label text-t2bb6ff mb-1">// AGGANCIA UGLY → PESDB</div>
            <h1 className="op-heading text-3xl text-tffffff mb-2">Aggancia i nuovi giocatori PESDB</h1>
            <p className="text-t8f95a3 text-sm mb-5 max-w-3xl">
                Dopo un <strong>Sync veloce</strong>, qui trovi le voci di rosa senza collegamento PESDB.
                Conferma l&apos;aggancio (per i nomi duplicati scegli quello giusto): da quel momento lo stipendio
                si ricalcola con <strong>VG e ruolo reali</strong>.
            </p>

            <div className="flex flex-wrap gap-3 mb-6">
                <StatPill label="Da agganciare" value={data.summary.total_unlinked} accent="rgb(var(--tc-ffc93c))" testId="link-total" />
                <StatPill label="Con corrispondenza" value={withMatch.length} accent="rgb(var(--tc-7ce38b))" testId="link-with-match" />
                <StatPill label="Non ancora nel PESDB" value={noMatch.length} accent="rgb(var(--tc-5c6370))" testId="link-no-match" />
                <StatPill label="Agganciati ora" value={linkedCount} accent="rgb(var(--tc-2bb6ff))" testId="link-done" />
                <button onClick={load} className="op-btn self-center" data-testid="link-reload-btn">↻ Ricarica</button>
            </div>

            {msg && <div className="op-mono text-xs text-t7ce38b mb-3" data-testid="link-msg">{msg}</div>}

            {/* CON CORRISPONDENZA */}
            <div className="op-label text-[10px] text-t7ce38b mb-2">// CORRISPONDENZA TROVATA ({withMatch.length})</div>
            {withMatch.length === 0 ? (
                <div className="op-card p-4 text-t8f95a3 text-sm mb-6">Nessuna voce con corrispondenza automatica. Usa la sezione manuale sotto.</div>
            ) : (
                <div className="space-y-2 mb-8">
                    {withMatch.map((e) => <LinkRow key={e.entry_id} entry={e} onLinked={onLinked} />)}
                </div>
            )}

            {/* SENZA CORRISPONDENZA */}
            <button
                onClick={() => setShowNoMatch((v) => !v)}
                className="op-label text-[10px] text-t8f95a3 mb-2 hover:text-tffffff"
                data-testid="toggle-no-match"
            >
                // NON ANCORA NEL PESDB ({noMatch.length}) — {showNoMatch ? "nascondi" : "mostra / aggancio manuale"}
            </button>
            {showNoMatch && (
                <div className="space-y-2">
                    {noMatch.map((e) => <ManualLinkRow key={e.entry_id} entry={e} onLinked={onLinked} />)}
                </div>
            )}
        </div>
    );
}

function StatPill({ label, value, accent, testId }) {
    return (
        <div className="op-card px-4 py-2" data-testid={testId}>
            <div className="op-label text-[9px] text-t8f95a3">{label}</div>
            <div className="op-heading text-xl" style={{ color: accent }}>{value}</div>
        </div>
    );
}

function EntryInfo({ entry }) {
    return (
        <div className="min-w-0">
            <div className="text-tffffff text-sm truncate">{entry.name}</div>
            <div className="op-mono text-[10px] text-t8f95a3">
                {entry.team_name || "—"}{entry.age != null ? ` · ${entry.age} anni` : ""}
                {entry.market_value ? ` · TM ${(entry.market_value / 1e6).toFixed(1)}mln` : ""}
            </div>
        </div>
    );
}

function LinkRow({ entry, onLinked }) {
    const [sel, setSel] = useState(entry.candidates[0]?.pes_id ?? "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const chosen = entry.candidates.find((c) => String(c.pes_id) === String(sel));

    const doLink = async () => {
        if (!sel) return;
        setBusy(true); setErr("");
        try {
            await api.post("/admin/pesdb-link/link", { entry_id: entry.entry_id, pes_id: Number(sel) });
            onLinked(entry.entry_id, `${entry.name} → ${chosen?.name} (VG ${chosen?.overall} · ${chosen?.position})`);
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore");
        } finally { setBusy(false); }
    };

    return (
        <div className="op-card p-3 flex items-center gap-3 flex-wrap" data-testid={`link-row-${entry.entry_id}`}>
            <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                {chosen?.image_url && <img src={chosen.image_url} alt="" className="w-8 h-8 object-contain shrink-0" />}
                <EntryInfo entry={entry} />
            </div>
            <select
                value={sel}
                onChange={(e) => setSel(e.target.value)}
                className="bg-t0d0f12 border border-t22252a px-2 py-1.5 text-tffffff text-xs min-w-[260px] focus:border-t2bb6ff focus:outline-none"
                data-testid={`link-select-${entry.entry_id}`}
            >
                {entry.candidates.map((c) => (
                    <option key={c.pes_id} value={c.pes_id}>
                        {c.name} · VG {c.overall} · {c.position}{c.club ? ` · ${c.club}` : ""}{c.is_duplicate ? " · DUP" : ""}{c.in_roster ? " · GIÀ A ROSA" : ""}
                    </option>
                ))}
            </select>
            {entry.candidates.length > 1 && (
                <span className="op-label text-[9px] text-tffc93c" title="Più candidati: scegli quello giusto">{entry.candidates.length} omonimi</span>
            )}
            <button onClick={doLink} disabled={busy} className="op-btn op-btn-primary disabled:opacity-50" data-testid={`link-confirm-${entry.entry_id}`}>
                {busy ? "…" : "Aggancia"}
            </button>
            {err && <span className="op-mono text-[10px] text-tff4d4d">{err}</span>}
        </div>
    );
}

function ManualLinkRow({ entry, onLinked }) {
    const [q, setQ] = useState(entry.name);
    const [results, setResults] = useState(null);
    const [busy, setBusy] = useState(false);

    const search = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/players", { params: { q, page_size: 8, page: 1, include_duplicates: 1, include_pes_free_agents: 1 } });
            setResults(data.items || []);
        } catch { setResults([]); } finally { setBusy(false); }
    };

    const link = async (p) => {
        await api.post("/admin/pesdb-link/link", { entry_id: entry.entry_id, pes_id: p.pes_id });
        onLinked(entry.entry_id, `${entry.name} → ${p.name} (VG ${p.overall ?? "?"} · ${p.position ?? "?"})`);
    };

    return (
        <div className="op-card p-3" data-testid={`manual-row-${entry.entry_id}`}>
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]"><EntryInfo entry={entry} /></div>
                <input
                    value={q} onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Cerca nel PESDB…"
                    className="bg-t0d0f12 border border-t22252a px-2 py-1.5 text-tffffff text-xs w-56 focus:border-t2bb6ff focus:outline-none"
                    data-testid={`manual-search-${entry.entry_id}`}
                />
                <button onClick={search} disabled={busy} className="op-btn disabled:opacity-50">{busy ? "…" : "Cerca"}</button>
            </div>
            {results && (
                <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                    {results.length === 0 && <div className="op-mono text-[10px] text-t8f95a3">Nessun risultato.</div>}
                    {results.map((p) => (
                        <button
                            key={p.pes_id}
                            onClick={() => link(p)}
                            className="flex items-center gap-2 p-2 border border-t22252a text-left text-xs hover:border-t2bb6ff transition-colors"
                            data-testid={`manual-pick-${entry.entry_id}-${p.pes_id}`}
                        >
                            {p.image_url && <img src={p.image_url} alt="" className="w-6 h-6 object-contain" />}
                            <span className="text-tffffff truncate flex-1">{p.name}</span>
                            <span className="op-mono text-[10px] text-t8f95a3">VG {p.overall ?? "?"} · {p.position ?? "?"}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
